import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Plus, Trash2, Edit, Download, ClipboardList, Save, Check } from "lucide-react";
import { toast } from "sonner";

const QUESTION_TYPES = [
  { value: "text", label: "Short text" },
  { value: "textarea", label: "Long text" },
  { value: "yes_no", label: "Yes / No" },
  { value: "select", label: "Choose one (dropdown)" },
];

export default function EventSurveyCard({ eventId, showAdminControls, eventTitle }) {
  const [loading, setLoading] = useState(true);
  const [survey, setSurvey] = useState(null);
  const [myResponse, setMyResponse] = useState(null);
  const [myPlayerId, setMyPlayerId] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [adminResponses, setAdminResponses] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(false);

  const fetchSurvey = async () => {
    try {
      const r = await apiClient.get(`/events/${eventId}/survey`);
      setSurvey(r.data.survey);
      setMyResponse(r.data.my_response);
      setMyPlayerId(r.data.my_player_id);
      if (r.data.my_response) {
        setAnswers(r.data.my_response.answers || {});
      } else {
        setAnswers({});
      }
    } catch (e) {
      console.error("Failed to load survey", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminResponses = async () => {
    if (!showAdminControls || !survey) return;
    setLoadingResponses(true);
    try {
      const r = await apiClient.get(`/events/${eventId}/survey/responses`);
      setAdminResponses(r.data || []);
    } catch (e) {
      console.error("Failed to load responses", e);
    } finally {
      setLoadingResponses(false);
    }
  };

  useEffect(() => {
    fetchSurvey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (showAdminControls && survey) fetchAdminResponses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdminControls, survey?.id]);

  const openBuilder = () => {
    setQuestions(
      (survey?.questions || []).map((q) => ({
        ...q,
        options_str: (q.options || []).join(", "),
      }))
    );
    setBuilderOpen(true);
  };

  const openNewBuilder = () => {
    setQuestions([
      { label: "", type: "text", required: false, options: null, options_str: "" },
    ]);
    setBuilderOpen(true);
  };

  const addQuestion = () => {
    if (questions.length >= 10) {
      toast.error("Maximum 10 questions per survey");
      return;
    }
    setQuestions([
      ...questions,
      { label: "", type: "text", required: false, options: null, options_str: "" },
    ]);
  };

  const updateQuestion = (idx, patch) => {
    setQuestions(questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };

  const removeQuestion = (idx) => {
    setQuestions(questions.filter((_, i) => i !== idx));
  };

  const saveSurvey = async () => {
    // Validate
    const cleaned = [];
    for (const q of questions) {
      const label = (q.label || "").trim();
      if (!label) {
        toast.error("Every question needs a label");
        return;
      }
      const entry = { id: q.id, label, type: q.type, required: !!q.required };
      if (q.type === "select") {
        const opts = (q.options_str || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        if (opts.length === 0) {
          toast.error(`"${label}" needs at least one option (comma-separated)`);
          return;
        }
        entry.options = opts;
      }
      cleaned.push(entry);
    }
    setSaving(true);
    try {
      await apiClient.put(`/events/${eventId}/survey`, { questions: cleaned });
      toast.success("Survey saved");
      setBuilderOpen(false);
      await fetchSurvey();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save survey");
    } finally {
      setSaving(false);
    }
  };

  const deleteSurvey = async () => {
    if (!confirm("Delete this survey and all player responses? This cannot be undone.")) return;
    try {
      await apiClient.delete(`/events/${eventId}/survey`);
      toast.success("Survey deleted");
      setSurvey(null);
      setMyResponse(null);
      setAdminResponses([]);
    } catch (e) {
      toast.error("Failed to delete survey");
    }
  };

  const submitAnswers = async () => {
    setSubmitting(true);
    try {
      const r = await apiClient.post(`/events/${eventId}/survey/response`, { answers });
      setMyResponse(r.data);
      toast.success(myResponse ? "Response updated" : "Response submitted");
      if (showAdminControls) fetchAdminResponses();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit response");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await apiClient.get(`/events/${eventId}/survey/responses.csv`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const safe = (eventTitle || "event").replace(/\s+/g, "_").slice(0, 40);
      a.download = `survey_${safe}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download CSV");
    }
  };

  if (loading) {
    return (
      <Card className="glass-card">
        <CardContent className="p-6 text-sm text-muted-foreground">Loading survey…</CardContent>
      </Card>
    );
  }

  // Empty state — no survey attached
  if (!survey) {
    return (
      <Card className="glass-card">
        <CardHeader className="border-b border-white/5 pb-4">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" />
            Survey
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            {showAdminControls
              ? "Attach a short questionnaire to this event — e.g. car registration plates for free parking, dietary requirements, T-shirt sizes."
              : "No survey attached to this event."}
          </p>
          {showAdminControls && (
            <Button onClick={openNewBuilder} data-testid="survey-create-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create survey
            </Button>
          )}
          {showAdminControls && (
            <SurveyBuilder
              open={builderOpen}
              onOpenChange={setBuilderOpen}
              questions={questions}
              addQuestion={addQuestion}
              updateQuestion={updateQuestion}
              removeQuestion={removeQuestion}
              onSave={saveSurvey}
              saving={saving}
              isNew={true}
            />
          )}
        </CardContent>
      </Card>
    );
  }

  // Survey attached
  return (
    <Card className="glass-card" data-testid="survey-card">
      <CardHeader className="border-b border-white/5 pb-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" />
            Survey
            <Badge variant="outline" className="ml-1">
              {survey.questions.length} question{survey.questions.length === 1 ? "" : "s"}
            </Badge>
          </CardTitle>
          {showAdminControls && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={openBuilder}
                data-testid="survey-edit-btn"
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadCsv}
                disabled={adminResponses.length === 0}
                data-testid="survey-csv-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-400"
                onClick={deleteSurvey}
                data-testid="survey-delete-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {/* My response form — only if linked to a player */}
        {myPlayerId && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-primary">
                {myResponse ? "Your response" : "Fill in your response"}
              </h3>
              {myResponse && (
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                  <Check className="w-3 h-3 mr-1" />
                  Submitted
                </Badge>
              )}
            </div>
            {survey.questions.map((q) => (
              <div key={q.id} className="space-y-2">
                <Label className="flex items-center gap-2">
                  {q.label}
                  {q.required && <span className="text-red-400">*</span>}
                </Label>
                <QuestionInput
                  q={q}
                  value={answers[q.id] ?? ""}
                  onChange={(val) => setAnswers({ ...answers, [q.id]: val })}
                />
              </div>
            ))}
            <Button
              onClick={submitAnswers}
              disabled={submitting}
              data-testid="survey-submit-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {submitting ? "Saving…" : myResponse ? "Update response" : "Submit response"}
            </Button>
          </div>
        )}
        {!myPlayerId && (
          <p className="text-sm text-muted-foreground">
            You're not linked to a player profile for this band, so you can't submit a response.
          </p>
        )}

        {/* Admin: responses table */}
        {showAdminControls && (
          <div className="border-t border-white/5 pt-6">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              All responses
              <Badge variant="outline">{adminResponses.length}</Badge>
            </h3>
            {loadingResponses ? (
              <p className="text-xs text-muted-foreground">Loading…</p>
            ) : adminResponses.length === 0 ? (
              <p className="text-xs text-muted-foreground">No responses yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="survey-responses-table">
                  <thead>
                    <tr className="text-left text-xs uppercase text-muted-foreground border-b border-white/5">
                      <th className="py-2 pr-4">Player</th>
                      {survey.questions.map((q) => (
                        <th key={q.id} className="py-2 pr-4">{q.label}</th>
                      ))}
                      <th className="py-2 pr-4">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminResponses.map((r) => (
                      <tr key={r.id} className="border-b border-white/5">
                        <td className="py-2 pr-4 font-medium">{r.player_name}</td>
                        {survey.questions.map((q) => (
                          <td key={q.id} className="py-2 pr-4 text-muted-foreground">
                            {r.answers?.[q.id] || <span className="opacity-40">—</span>}
                          </td>
                        ))}
                        <td className="py-2 pr-4 text-xs text-muted-foreground">
                          {formatDate(r.updated_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {showAdminControls && (
          <SurveyBuilder
            open={builderOpen}
            onOpenChange={setBuilderOpen}
            questions={questions}
            addQuestion={addQuestion}
            updateQuestion={updateQuestion}
            removeQuestion={removeQuestion}
            onSave={saveSurvey}
            saving={saving}
            isNew={false}
          />
        )}
      </CardContent>
    </Card>
  );
}

function QuestionInput({ q, value, onChange }) {
  if (q.type === "textarea") {
    return (
      <Textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        maxLength={500}
        rows={3}
        data-testid={`survey-answer-${q.id}`}
      />
    );
  }
  if (q.type === "yes_no") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={`survey-answer-${q.id}`}>
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="yes">Yes</SelectItem>
          <SelectItem value="no">No</SelectItem>
        </SelectContent>
      </Select>
    );
  }
  if (q.type === "select") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={`survey-answer-${q.id}`}>
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          {(q.options || []).map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  // text
  return (
    <Input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      maxLength={500}
      data-testid={`survey-answer-${q.id}`}
    />
  );
}

function SurveyBuilder({
  open,
  onOpenChange,
  questions,
  addQuestion,
  updateQuestion,
  removeQuestion,
  onSave,
  saving,
  isNew,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "Create survey" : "Edit survey"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {questions.map((q, idx) => (
            <div
              key={idx}
              className="border border-white/10 rounded-lg p-4 space-y-3"
              data-testid={`survey-question-row-${idx}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Question {idx + 1}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-400 h-7"
                  onClick={() => removeQuestion(idx)}
                  data-testid={`survey-remove-q-${idx}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Question text</Label>
                <Input
                  value={q.label}
                  onChange={(e) => updateQuestion(idx, { label: e.target.value })}
                  placeholder="e.g. Car registration plate"
                  data-testid={`survey-q-label-${idx}`}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">Answer type</Label>
                  <Select
                    value={q.type}
                    onValueChange={(v) =>
                      updateQuestion(idx, {
                        type: v,
                        options_str: v === "select" ? q.options_str : "",
                      })
                    }
                  >
                    <SelectTrigger data-testid={`survey-q-type-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {QUESTION_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <Checkbox
                      checked={q.required}
                      onCheckedChange={(v) => updateQuestion(idx, { required: !!v })}
                      data-testid={`survey-q-required-${idx}`}
                    />
                    Required
                  </label>
                </div>
              </div>
              {q.type === "select" && (
                <div className="space-y-2">
                  <Label className="text-xs">Options (comma-separated)</Label>
                  <Input
                    value={q.options_str || ""}
                    onChange={(e) => updateQuestion(idx, { options_str: e.target.value })}
                    placeholder="Small, Medium, Large"
                    data-testid={`survey-q-options-${idx}`}
                  />
                </div>
              )}
            </div>
          ))}
          {questions.length < 10 && (
            <Button
              variant="outline"
              onClick={addQuestion}
              className="w-full"
              data-testid="survey-add-q-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add question
            </Button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving} data-testid="survey-save-btn">
            {saving ? "Saving…" : "Save survey"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}
